import { Button } from "@/components/Button/Button";
import { CloseSvg } from "@/components/Icon/Icon";
import { Box, IconButton, LinearProgress, Modal, SelectChangeEvent, TextField, Theme, Typography } from "@mui/material";
import { Dispatch, SetStateAction, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import { DialogBox } from "@/components/DialogBox/DialogBox";
import { toothComponents } from "@/components/Icon/ToothMapping";
import { LabelledCheckbox } from "@/components/LabelledCheckbox/LabelledCheckbox";
import { LabelledDropdown } from "@/components/LabelledDropdown/LabelledDropdown";
import { StyledTooltip, TooltipBodyWithHeader } from "@/components/StyledTooltip/StyledTooltip";
import { changeTemplate, TemplateSliceToCaseOptions } from "@/pages/Home/DesignSection/AssembledTabsSection/EditTemplateModal/utils";
import {
  ModelAttachmentTypes,
  ModelTextStyle,
  useCreateTemplateApiV1TemplatesPostMutation,
  useDeleteTemplateApiV1TemplatesTemplateIdDeleteMutation,
  useUpdateTemplateApiV1TemplatesTemplateIdPutMutation
} from "@/store/baseClientApi";
import {
  defaultTemplate,
  selectIsNameInvalid,
  selectModelType,
  selectTemplate,
  selectTemplateAttachments,
  selectTemplateIsHollow,
  selectTemplateName,
  selectTemplateTextStyle,
  setIsAffixText,
  setIsHollow,
  setModelAttachmentType,
  setModelType,
  setName,
  setTemplate,
  setTextStyle
} from "@/store/slices/templateSlice";
import { store } from "@/store/store";
import { ensureError, localizeError } from "@/utils/helpers";
import toast from "react-hot-toast";

const Header = ({ onClick, action }: { onClick: () => void; action: TemplateModelAction }) => {
  const { t } = useTranslation("template");

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        backgroundColor: "white",
        borderRadius: "8px 8px 0px 0px",
        padding: "16px",
        paddingLeft: "32px",
        borderBottom: (theme: Theme) => `1px solid ${theme.outline.main}`
      }}
    >
      <Typography
        sx={{
          fontFamily: "Montserrat, sans-serif",
          fontWeight: 500,
          fontSize: "24px"
        }}
      >
        {action === "add" ? t("text.addTemplate") : t("text.editTemplate")}
      </Typography>
      <IconButton
        onClick={onClick}
        sx={{
          height: "32px",
          borderRadius: "100px"
        }}
      >
        <CloseSvg color="inherit" />
      </IconButton>
    </Box>
  );
};

const Footer = ({ action, handleClose }: { handleClose: () => void; action: TemplateModelAction }) => {
  const { t } = useTranslation("template");
  const [isProcessing, setIsProcessing] = useState(false);
  const [showNameDupError, setShowNameDupError] = useState(false);
  const [showDeleteWarning, setShowDeleteWarning] = useState(false);
  const submitDisabled = useSelector(selectIsNameInvalid) || isProcessing;
  const [addTemplate] = useCreateTemplateApiV1TemplatesPostMutation();
  const [saveTemplate] = useUpdateTemplateApiV1TemplatesTemplateIdPutMutation();
  const [deleteTemplate] = useDeleteTemplateApiV1TemplatesTemplateIdDeleteMutation();

  const handleSubmit = async () => {
    const template = selectTemplate(store.getState());

    setIsProcessing(true);
    const templateRequest = {
      name: template.name,
      case_options: TemplateSliceToCaseOptions(template)
    };

    try {
      switch (action) {
        case "add": {
          const resp = await addTemplate({
            templateCreatePublic: templateRequest
          }).unwrap();
          changeTemplate(resp);
          break;
        }
        case "edit":
          if (template.templateId === undefined) {
            setIsProcessing(false);
            return;
          }
          await saveTemplate({
            templateId: template.templateId,
            templateUpdatePublic: templateRequest
          }).unwrap();
          break;
      }
      handleClose();
    } catch (err) {
      const error = ensureError(err);
      if (error.status === 409 && error.default_message === "name already exists") {
        setShowNameDupError(true);
      } else {
        toast.error(localizeError(t, error));
      }
    }
    setIsProcessing(false);
  };

  const promptForDelete = () => setShowDeleteWarning(true);

  const handleDelete = async () => {
    const template = selectTemplate(store.getState());
    if (template.templateId === undefined) {
      return;
    }
    try {
      await deleteTemplate({
        templateId: template.templateId
      }).unwrap();
      store.dispatch(setTemplate({ ...defaultTemplate }));
      handleClose();
    } catch (err) {
      toast.error(localizeError(t, ensureError(err)));
    }
  };

  return (
    <>
      <Box
        sx={{
          padding: "16px",
          border: "1px",
          gap: "16px",
          height: "fit-content",
          radiusBottom: "16px",
          width: "100%",
          backgroundColor: "white",
          borderRadius: "0px 0px 8px 8px",
          borderTop: (theme: Theme) => `1px solid ${theme.outline.main}`
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <Box sx={{ display: "flex", justifyContent: "start", gap: "12px" }}>
            <Button
              variant="contained"
              color="error"
              sx={{
                borderRadius: "100px",
                height: "32px",
                display: action === "edit" ? "block" : "none"
              }}
              onClick={promptForDelete}
            >
              {t("button.delete")}
            </Button>
          </Box>
          <LinearProgress variant="determinate" value={50} />
          <Box sx={{ display: "flex", justifyContent: "end", gap: "12px" }}>
            <Button
              variant="contained"
              color={submitDisabled ? "error" : "secondary"}
              sx={{
                borderRadius: "100px",
                height: "32px"
              }}
              disabled={submitDisabled}
              onClick={handleSubmit}
            >
              {action === "add" ? t("button.add") : t("button.update")}
            </Button>
          </Box>
        </Box>
      </Box>
      <DialogBox
        title={t("title.renameTemplate")}
        open={showNameDupError}
        setOpen={setShowNameDupError}
        args={undefined}
        message={<Typography>{t("message.renameTemplate")}</Typography>}
        onClose={() => {
          return;
        }}
        sx={{ ".MuiPaper-root": { width: "100%", margin: "16px" } }}
        hideDecline
        confirmText={t("common:button.ok")}
      />
      <DialogBox
        title={t("title.deleteTemplate")}
        open={showDeleteWarning}
        setOpen={setShowDeleteWarning}
        args={undefined}
        message={<Typography>{t("message.deleteTemplate")}</Typography>}
        onClose={(confirm) => {
          if (confirm) {
            handleDelete();
          }
        }}
        sx={{ ".MuiPaper-root": { width: "100%", margin: "16px" } }}
      />
    </>
  );
};

const TemplateName = () => {
  const { t } = useTranslation("template");
  const nameRef = useRef<HTMLInputElement>(null);

  const name = useSelector(selectTemplateName);
  const isNameInvalid = useSelector(selectIsNameInvalid);

  const handleNameChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
    const name = e.target.value.trim();
    store.dispatch(setName(name));
  };

  return (
    <StyledTooltip
      placement="right"
      title={<TooltipBodyWithHeader body={t("message.templateNameHelp")} header={t("title.templateName")} />}
    >
      <Box sx={{ display: "flex", justifyContent: "flex-start", gap: "8px", height: "fit-content" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center"
          }}
        >
          <Typography
            sx={{
              fontFamily: "Montserrat, sans-serif",
              fontWeight: 400,
              fontSize: "18px",
              width: "180px",
              height: "22px"
            }}
          >
            {t("title.templateName")}
          </Typography>
        </Box>
        <TextField
          fullWidth
          inputRef={nameRef}
          variant="outlined"
          onChange={handleNameChanged}
          error={isNameInvalid}
          defaultValue={name ?? ""}
          sx={{
            paddingLeft: "8px",
            ...(isNameInvalid && {
              borderColor: "red" // Highlight with red border if invalid
            }),
            "& .MuiOutlinedInput-root": {
              borderRadius: "4px",
              height: "36px",
              display: "flex",
              alignItems: "center"
            },
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: isNameInvalid ? "red" : undefined // Change border color if invalid
            },
            "& .MuiInputLabel-root": {
              top: "-10px" // Adjusts label positioning to center with input
            },
            "& .MuiOutlinedInput-input::placeholder": {
              // Text for empty field error
              color: "red",
              opacity: 1
            }
          }}
        />
      </Box>
    </StyledTooltip>
  );
};

const TemplateModelType = () => {
  const { t } = useTranslation("template");
  const modelType = useSelector(selectModelType);

  const handleModelTypeChanged = (e: SelectChangeEvent<string>) => {
    store.dispatch(setModelType(e.target.value as "horse_shoe" | "full_palate" | undefined));
  };

  return (
    <LabelledDropdown
      label={t("label.modelType")}
      tooltip={<TooltipBodyWithHeader body={t("message.modelTypeHelp")} header={t("label.modelType")} />}
      options={[
        { value: "horse_shoe", label: t("options.modelType.horseShoe") },
        { value: "full_palate", label: t("options.modelType.fullPalate") }
      ]}
      value={modelType ?? "horse_shoe"}
      onChange={handleModelTypeChanged}
    />
  );
};

const TemplateAttachments = () => {
  const { t } = useTranslation("template");
  const attachments = useSelector(selectTemplateAttachments);

  const handleAttachmentsChanged = (e: SelectChangeEvent<string>) => {
    if (e.target.value === "none") {
      store.dispatch(setModelAttachmentType(undefined));
      return;
    }

    store.dispatch(setModelAttachmentType(e.target.value as ModelAttachmentTypes));
  };

  return (
    <LabelledDropdown
      label={t("label.modelAttachmentTypes")}
      tooltip={<TooltipBodyWithHeader body={t("message.attachmentHelp")} header={t("label.modelAttachmentTypes")} />}
      options={[
        { value: "none", label: t("options.modelAttachmentTypes.none") },
        { value: "pillars", label: t("options.modelAttachmentTypes.pillars") }
      ]}
      // If formValues.attachments is not set, fallback to the default from defaultTemplate
      value={attachments ?? "none"}
      onChange={handleAttachmentsChanged}
    />
  );
};

const TemplateIsHollow = () => {
  const { t } = useTranslation("template");
  const isHollow = useSelector(selectTemplateIsHollow);

  const handleIsHollowChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
    store.dispatch(setIsHollow(e.target.checked));
  };

  return (
    <>
      <LabelledCheckbox
        tooltip={<TooltipBodyWithHeader body={t("message.hollowModelHelp")} header={t("label.hollowModel")} />}
        label={t("label.hollowModel") + ":"}
        checked={isHollow}
        onChange={handleIsHollowChanged}
      />
      <LabelledCheckbox
        tooltip={<TooltipBodyWithHeader body={t("message.drainHolesHelp")} header={t("label.drainHoles")} />}
        label={t("label.drainHoles") + ":"}
        disabled={true}
        checked={isHollow ? true : false} // Only enable if on hollow is enabled
      />
    </>
  );
};

const TemplateTextStyle = () => {
  const { t } = useTranslation("template");
  const textStyle = useSelector(selectTemplateTextStyle);
  const isTextAffixed = textStyle !== undefined;

  const handleIsAffixTextChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
    store.dispatch(setIsAffixText(e.target.checked));
  };

  const handleTextStyleChanged = (e: SelectChangeEvent<string>) => {
    store.dispatch(setTextStyle(e.target.value as ModelTextStyle));
  };

  return (
    <>
      <LabelledCheckbox
        tooltip={<TooltipBodyWithHeader body={t("message.affixTextHelp")} header={t("label.affixText")} />}
        label={t("label.affixText") + ":"}
        checked={isTextAffixed}
        onChange={handleIsAffixTextChanged}
      />
      <Box style={{ visibility: isTextAffixed ? "visible" : "hidden" }}>
        <LabelledDropdown
          tooltip={<TooltipBodyWithHeader body={t("message.textStyleHelp")} header={t("label.textStyle")} />}
          label={t("label.textStyle")}
          options={[
            { value: "embossed", label: t("options.textStyle.embossed") },
            { value: "engraved", label: t("options.textStyle.engraved") }
          ]}
          value={textStyle ?? "embossed"}
          onChange={handleTextStyleChanged}
        />
      </Box>
    </>
  );
};

const ModelIcon = () => {
  const { t } = useTranslation("template");
  const isHollow = useSelector(selectTemplateIsHollow);
  const attachments = useSelector(selectTemplateAttachments);
  const textStyle = useSelector(selectTemplateTextStyle);
  const modelType = useSelector(selectModelType);
  const hollowText: "hollow" | "solid" = isHollow ? "hollow" : "solid";
  const attachmentsText: "pillars" | "none" = attachments === "pillars" ? "pillars" : "none";
  const affixedTextText: ModelTextStyle | "none" = textStyle === undefined ? "none" : textStyle;
  const modelTypeText: "palate" | "none" = modelType === "full_palate" ? "palate" : "none";

  const fileName = `${hollowText}-${attachmentsText}-${affixedTextText}-${modelTypeText}.svg`;
  const ToothComponent = toothComponents[fileName] || toothComponents["hollow-none-none-none.svg"];

  return (
    <Box
      sx={{
        width: "262px",
        height: "247px",
        borderRadius: "50%",
        display: "flex",
        fontSize: "24px",
        fontWeight: "bold"
      }}
    >
      <ToothComponent title={t("alt.modelIcon")} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
    </Box>
  );
};

interface EditTemplateModalProps {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  action: TemplateModelAction;
}

export type TemplateModelAction = "add" | "edit";

export const EditTemplateModal = ({ open, setOpen, action }: EditTemplateModalProps) => {
  const handleClose = () => {
    setOpen(false);
  };

  return (
    <Box sx={{ width: "fit-content", display: "flex", justifyContent: "center", alignItems: "center" }}>
      <Modal open={open} onClose={handleClose} sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
        <Box
          component="div"
          sx={{
            width: "740px",
            height: "fit-content",
            borderRadius: "8px"
          }}
        >
          {/* Header */}
          <Header onClick={handleClose} action={action} />

          {/* Modal Body */}
          <Box
            sx={{
              padding: "24px",
              paddingLeft: "32px",
              height: "fit-content",
              backgroundColor: "white"
            }}
          >
            {/* Template Details */}
            <Box sx={{ display: "flex", paddingRight: "24px", gap: "24px", height: "fit-content", width: "100%" }}>
              {/* LHS */}
              <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                <TemplateName />
                <TemplateModelType />
                <TemplateAttachments />
                <TemplateIsHollow />
                <TemplateTextStyle />
              </Box>

              {/* RHS */}
              <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", width: "fit-content" }}>
                <ModelIcon />
              </Box>
            </Box>
          </Box>

          {/* Footer */}
          <Footer handleClose={handleClose} action={action} />
        </Box>
      </Modal>
    </Box>
  );
};
